import { LinearProgress, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Card, Flex, FlexColumn, Link } from '@elementary/common-ui/components';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';

const SUPPORT_MAILTO = 'mailto:support@elementary-data.com';

type Props = {
  creditsUsed: number;
  creditLimit: number | null;
  resetsAt: string;
};

const LimitReachedChip = () => (
  <Flex
    sx={{
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 1.5,
      backgroundColor: 'common.orange10',
    }}
  >
    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: 'common.orange100' }} />
    <Typography sx={{ fontSize: 14 }}>
      Credit limit reached. <Link href={SUPPORT_MAILTO}>Contact us.</Link>
    </Typography>
  </Flex>
);

const ResetsAt = ({ resetsAt }: { resetsAt: string }) => (
  <Typography sx={{ fontSize: 13, color: 'common.gray2' }}>
    Resets on {localizeDate(resetsAt).format('LL')}, at{' '}
    {localizeDate(resetsAt).format('LT')}.
  </Typography>
);

const MonthlyCreditsCard = ({ creditsUsed, creditLimit, resetsAt }: Props) => {
  const used = Math.round(creditsUsed);

  if (creditLimit === null) {
    return (
      <Card>
        <FlexColumn sx={{ gap: 1.5, width: '100%' }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
            Monthly credit usage
          </Typography>
          <Typography sx={{ fontWeight: 'bold', fontSize: 28 }}>
            {numberWithCommas(used)}
          </Typography>
          <ResetsAt resetsAt={resetsAt} />
        </FlexColumn>
      </Card>
    );
  }

  const pct =
    creditLimit > 0
      ? Math.min(100, Math.round((used / creditLimit) * 100))
      : 100;
  const isLimitReached = used >= creditLimit;

  return (
    <Card>
      <FlexColumn sx={{ gap: 1.5, width: '100%' }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
          Monthly credit usage
        </Typography>
        <Flex sx={{ alignItems: 'center', gap: 2 }}>
          <Flex sx={{ alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: 28 }}>
              {pct}%
            </Typography>
            <Typography sx={{ fontSize: 28, color: 'common.gray2' }}>
              {numberWithCommas(used)} / {numberWithCommas(creditLimit)}
            </Typography>
          </Flex>
          {isLimitReached && <LimitReachedChip />}
        </Flex>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <ResetsAt resetsAt={resetsAt} />
      </FlexColumn>
    </Card>
  );
};

export default MonthlyCreditsCard;
